import { useMutation } from '@tanstack/react-query';

import { useProjectsApi } from 'api/data/projects/projects-api';
import { resolve } from 'api/resolve';
import { SNACKBAR_STORE } from 'state/snackbar/snackbarStore';

import { useDecompositionRelation } from './useDecompositionRelation';

/**
 * Adds a child node via the atomic create-child endpoint (`addNode` with
 * `parentNodeId`/`relationId`). The backend creates the node and links it to
 * its parent in a single transaction, so there is no orphan-on-partial-failure
 * risk and no separate relation call is needed.
 */
export function useAddChildNode(projectId: string, templateNodeId: string, proposalId?: string) {
  const { addNode } = useProjectsApi();

  const { relationId: templateRelationId } = useDecompositionRelation(templateNodeId);

  return useMutation({
    mutationFn: async ({ endDataNodeId }: { endDataNodeId: string }) => {
      // Without a decomposable relation the backend creates the node but silently
      // skips linking it to its parent (no error) — refuse before hitting the network.
      if (!templateRelationId) {
        throw new Error('No decomposable relation found for template node');
      }
      const response = await resolve(addNode(projectId, templateNodeId, proposalId, endDataNodeId, templateRelationId));
      return { createdNodeId: response.properties?.Id };
    },
    onError: () => {
      SNACKBAR_STORE.getState().showSnackbar(
        'Failed to add child node. Please try again or contact support.',
        'error',
        true
      );
    }
  });
}
